/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import qs from 'query-string'

import {
  TabbedView,
  MetricComparisonTable,
  LatestKPIMetrics,
  EmployerKPIMetrics
} from '../components'

import {
  METRIC_PICSA_DAILY_SUMMARY,
  METRIC_INVESTMENT_DAILY_SUMMARY,
  METRIC_CREDIT_DAILY_SUMMARY
} from '../constants'

const tabs = ['Latest', 'Clients', 'Investments', 'Credit', 'Employer KPI']

const getTabIndexFromQueryString = (queryString) => {
  if (queryString.length > 0) {
    let queryParams = qs.parse(queryString)
    if (queryParams.tab) {
      const tabName = queryParams.tab
      const tabIndex = tabs.map(tab => tab.toLowerCase()).indexOf(tabName)

      if (tabIndex > -1) {
        return tabIndex
      } else {
        return 0
      }
    } else {
      return 0
    }
  } else {
    return 0
  }
}


export default class Metrics extends React.PureComponent {
  state = {
    activeTabIndex: getTabIndexFromQueryString(this.props.location.search)
  }

  componentDidMount() {
    if (this.props.location.search.length > 0) {
      let queryParams = qs.parse(this.props.location.search)
      let tabName = queryParams.tab || null

      let tabIndex = tabName
        ? tabs.map(tab => tab.toLowerCase()).indexOf(tabName)
        : null

      if (tabIndex === -1) {
        // if url search param 'tab' doesn't match one of the expected options, set to 'latest'
        // NOTE that state will already be consistent with the url as if gets sets in getInitialTabIndex on load
        queryParams.tab = 'latest'
        this.props.history.replace({ search: qs.stringify(queryParams) })
      } else if (tabIndex === null) {
        let existingQueryParamsPlusTab = Object.assign(queryParams, { tab: 'latest' })
        this.props.history.replace({ search: qs.stringify(existingQueryParamsPlusTab) })
      }
    } else {
      this.props.history.replace({ search: qs.stringify({ tab: 'latest' }) })
    }
  }

  componentDidUpdate(prevProps, prevState) {
    let queryParams = qs.parse(this.props.location.search)
    if (prevState.activeTabIndex !== this.state.activeTabIndex) {
      queryParams.tab = tabs[this.state.activeTabIndex].toLowerCase()
      this.props.history.push({ search: qs.stringify(queryParams) })
    } else if (prevProps.location.search !== this.props.location.search) {
      // this condition will only be satisfied when user is on either 'clients' or 'investments' tab,
      // and then uses global navigation to navigate to same page; tab will remain the same
      let existingQueryParamsPlusTab = Object.assign(queryParams, { tab: tabs[this.state.activeTabIndex].toLowerCase() })
      this.props.history.replace({ search: qs.stringify(existingQueryParamsPlusTab) })
    }
  }

  changeActiveTab = (tabIndex) => (
    this.setState({ activeTabIndex: tabIndex }, this.props.history.replace({ search: '' }))  // changing tabs resets search params
  )

  render() {
    return (
      <div className='stage-main'>
        <TabbedView
          tabs={tabs}
          contents={[
            <LatestKPIMetrics metricSourceId={METRIC_PICSA_DAILY_SUMMARY} />,
            <MetricComparisonTable
              metricSourceId={METRIC_PICSA_DAILY_SUMMARY}
              className='slds-m-around_medium'
              renderTemplate={{
                "KPIs": {
                  "Clients": 'NumberField',
                  "Active Clients": 'NumberField',
                  "EarnBacks Only": 'NumberField',
                  "Active - Credit": 'NumberField',
                  "Active Employees": 'NumberField',
                  "EarnBacks Members": 'NumberField',
                  "Active - Insurance": 'NumberField',
                  "Active Non-members": 'NumberField',
                  "Eligible Employees": 'NumberField',
                  "Active - Investments": 'NumberField',
                  "EarnBacks Members - Outstanding": 'NumberField'
                },
                "Payroll": {
              		"Frequency": {
              			"null": 'NumberField',
              			"Every week": 'NumberField',
              			"Once a month": 'NumberField',
              			"Every second week": 'NumberField'
              		}
              	},
              	"Solution": {
              		"Package": {
              			"null": 'NumberField',
              			"basic": 'NumberField',
              			"premium": 'NumberField',
              			"standard": 'NumberField'
              		}
              	},
              	"EarnBacks": {
              		"null": 'NumberField',
              		"Active": 'NumberField',
              		"Inactive": 'NumberField',
              		"Ineligible": 'NumberField',
              		"Outstanding": 'NumberField'
              	},
              	"Employment": {
              		"Status": {
              			"Active": 'NumberField',
              			"Pending": 'NumberField',
              			"Suspended": 'NumberField',
              			"Non-active": 'NumberField',
              			"Terminated - Quit": 'NumberField',
              			"Terminated - Fired": 'NumberField',
              			"Terminated - Other": 'NumberField',
              			"Terminated - Retired": 'NumberField',
              			"Terminated - Deceased": 'NumberField',
              			"Terminated - Absconded": 'NumberField',
              			"Terminated - Seasonal Retrenched": 'NumberField',
              			"Terminated - Permanent Retrenched": 'NumberField'
              		}
              	},
              	"Membership": {
              		"Member": 'NumberField',
              		"Non-Member": 'NumberField'
                },
                "Health Level": {
                  "0": "NumberField",
                  "1": "NumberField",
                  "2": "NumberField",
                  "4": "NumberField"
                }
              }}
            />,
            <MetricComparisonTable
              metricSourceId={METRIC_INVESTMENT_DAILY_SUMMARY}
              className='slds-m-around_medium'
              renderTemplate={{
                "Long-term": {
                  "Units": "DecimalField",
                  "Unit Price": "CurrencyField",
                  "Balance - Bucket": {
                    "0,00": 'NumberField',
                    "0.01-9.99": 'NumberField',
                    "10000.00+": 'NumberField',
                    "10.00-99.99": 'NumberField',
                    "100.00-999.99": 'NumberField',
                    "1000.00-9999.99": 'NumberField'
                  },
                  "Balance - Invested": "CurrencyField",
                  "Balance - In Process": "CurrencyField",
                  "Contribution - Client": "CurrencyField",
                  "Contribution - Employer": "CurrencyField",
                  "Contribution Bucket - Client": {
                    "0.00": "NumberField",
                    "0.01-9.99": "NumberField",
                    "10.00-24.99": "NumberField",
                    "25.00-49.99": "NumberField",
                    "50.00-99.99": "NumberField",
                    "100.00-199.99": "NumberField",
                    "200.00-499.99": "NumberField",
                    "500.00+": "NumberField"
                  },
                  "Contribution Bucket - Employer": {
                    "0.00": "NumberField",
                    "0.01-9.99": "NumberField",
                    "10.00-24.99": "NumberField",
                    "25.00-49.99": "NumberField",
                    "50.00-99.99": "NumberField",
                    "100.00-199.99": "NumberField"
                  },
                  "Contributions In Process - Count": "NumberField",
                  "Contributions In Process - Value": "DecimalField",
                  "Contributions Count - Last Day": "NumberField",
                  "Contributions Value - Last Day": "DecimalField",
                  "Contributions Count - Last 7 Days": "NumberField",
                  "Contributions Value - Last 7 Days": "DecimalField",
                  "Contributions Count - Last 30 Days": "NumberField",
                  "Contributions Value - Last 30 Days": "DecimalField",
                  "Contributions Count - Last 90 Days": "NumberField",
                  "Contributions Value - Last 90 Days": "DecimalField",
                  "Withdrawals Count - Last Day": "NumberField",
                  "Withdrawals Value - Last Day": "DecimalField",
                  "Withdrawals Count - Last 7 Days": "NumberField",
                  "Withdrawals Value - Last 7 Days": "DecimalField",
                  "Withdrawals Count - Last 30 Days": "NumberField",
                  "Withdrawals Value - Last 30 Days": "DecimalField",
                  "Withdrawals Count - Last 90 Days": "NumberField",
                  "Withdrawals Value - Last 90 Days": "DecimalField",
                },
                "Short-term": {
                  "Name": {
                    "Allan Gray Money Market": 'NumberField'
                  },
                  "Units": "DecimalField",
                  "Unit Price": "CurrencyField",
                  "Balance - Bucket": {
                    "0.00": 'NumberField',
                    "0.01-9.99": 'NumberField',
                    "10.00-99.99": 'NumberField',
                    "100.00-999.99": 'NumberField',
                    "1000.00-9999.99": 'NumberField',
                    "10000.00+": 'NumberField',
                  },
                  "Balance - Invested": "CurrencyField",
                  "Balance - In Process": "CurrencyField",
                  "Contribution - Client": "CurrencyField",
                  "Contribution - Employer": "CurrencyField",
                  "Contribution Bucket - Client": {
                    "0.00": "NumberField",
                    "0.01-9.99": "NumberField",
                    "10.00-24.99": "NumberField",
                    "25.00-49.99": "NumberField",
                    "50.00-99.99": "NumberField",
                    "100.00-199.99": "NumberField",
                    "200.00-499.99": "NumberField",
                    "500.00+": "NumberField"
                  },
                  "Contribution Bucket - Employer": {
                    "0.00": "NumberField",
                    "10.00-24.99": "NumberField",
                    "25.00-49.99": "NumberField"
                  },
                  "Contributions In Process - Count": "NumberField",
                  "Contributions In Process - Value": "DecimalField",
                  "Contributions Count - Last Day": "NumberField",
                  "Contributions Value - Last Day": "DecimalField",
                  "Contributions Count - Last 7 Days": "NumberField",
                  "Contributions Value - Last 7 Days": "DecimalField",
                  "Contributions Count - Last 30 Days": "NumberField",
                  "Contributions Value - Last 30 Days": "DecimalField",
                  "Contributions Count - Last 90 Days": "NumberField",
                  "Contributions Value - Last 90 Days": "DecimalField",
                  "Withdrawals Count - Last Day": "NumberField",
                  "Withdrawals Value - Last Day": "DecimalField",
                  "Withdrawals Count - Last 7 Days": "NumberField",
                  "Withdrawals Value - Last 7 Days": "DecimalField",
                  "Withdrawals Count - Last 30 Days": "NumberField",
                  "Withdrawals Value - Last 30 Days": "DecimalField",
                  "Withdrawals Count - Last 90 Days": "NumberField",
                  "Withdrawals Value - Last 90 Days": "DecimalField"
                },
                "Investor Type": {
                  "None": 'NumberField',
                  "Client Only": 'NumberField',
                  "Employer Only": 'NumberField',
                  "Client and Employer": 'NumberField'
                },
                "Long-term - Employee": {
                  "Name": {
                    "Allan Gray EE Balanced A": 'NumberField'
                  },
                  "Units": "DecimalField",
                  "Unit Price": "CurrencyField",
                  "Balance - Bucket": {
                    "0.00": 'NumberField',
                    "0.01-9.99": 'NumberField',
                    "10.00-99.99": 'NumberField',
                    "100.00-999.99": 'NumberField',
                    "1000.00-9999.99": 'NumberField',
                    "10000.00+": 'NumberField',
                  },
                  "Balance - Invested": "CurrencyField",
                  "Balance - In Process": "CurrencyField",
                  "Contribution - Client": "CurrencyField",
                  "Contribution Bucket - Client": {
                    "0.00": 'NumberField',
                    "0.01-9.99": 'NumberField',
                    "10.00-24.99": 'NumberField',
                    "25.00-49.99": 'NumberField',
                    "50.00-99.99": 'NumberField',
                    "100.00-199.99": 'NumberField',
                    "200.00-499.99": 'NumberField',
                    "500.00+": 'NumberField'
                  },
                  "Contributions In Process - Count": "NumberField",
                  "Contributions In Process - Value": "DecimalField",
                  "Contributions Count - Last Day": "NumberField",
                  "Contributions Value - Last Day": "DecimalField",
                  "Contributions Count - Last 7 Days": "NumberField",
                  "Contributions Value - Last 7 Days": "DecimalField",
                  "Contributions Count - Last 30 Days": "NumberField",
                  "Contributions Value - Last 30 Days": "DecimalField",
                  "Contributions Count - Last 90 Days": "NumberField",
                  "Contributions Value - Last 90 Days": "DecimalField",
                  "Withdrawals Count - Last Day": "NumberField",
                  "Withdrawals Value - Last Day": "DecimalField",
                  "Withdrawals Count - Last 7 Days": "NumberField",
                  "Withdrawals Value - Last 7 Days": "DecimalField",
                  "Withdrawals Count - Last 30 Days": "NumberField",
                  "Withdrawals Value - Last 30 Days": "DecimalField",
                  "Withdrawals Count - Last 90 Days": "NumberField",
                  "Withdrawals Value - Last 90 Days": "DecimalField"
                },
                "Long-term - Employer": {
                  "Name": {
                    "Allan Gray ER Balanced A": 'NumberField'
                  },
                  "Units": "DecimalField",
                  "Unit Price": "CurrencyField",
                  "Balance - Bucket": {
                    "0.00": 'NumberField',
                    "0.01-9.99": 'NumberField',
                    "10.00-99.99": 'NumberField',
                    "100.00-999.99": 'NumberField',
                    "1000.00-9999.99": 'NumberField',
                    "10000.00+": 'NumberField'
                  },
                  "Balance - Invested": "CurrencyField",
                  "Balance - In Process": "CurrencyField",
                  "Contribution - Employer": "CurrencyField",
                  "Contribution Bucket - Employer": {
                    "0.00": "NumberField",
                    "0.01-9.99": "NumberField",
                    "10.00-24.99": "NumberField",
                    "25.00-49.99": "NumberField",
                    "50.00-99.99": "NumberField",
                    "100.00-199.99": "NumberField"
                  },
                  "Contributions In Process - Count": "NumberField",
                  "Contributions In Process - Value": "DecimalField",
                  "Contributions Count - Last Day": "NumberField",
                  "Contributions Value - Last Day": "DecimalField",
                  "Contributions Count - Last 7 Days": "NumberField",
                  "Contributions Value - Last 7 Days": "DecimalField",
                  "Contributions Count - Last 30 Days": "NumberField",
                  "Contributions Value - Last 30 Days": "DecimalField",
                  "Contributions Count - Last 90 Days": "NumberField",
                  "Contributions Value - Last 90 Days": "DecimalField",
                  "Withdrawals Count - Last Day": "NumberField",
                  "Withdrawals Value - Last Day": "DecimalField",
                  "Withdrawals Count - Last 7 Days": "NumberField",
                  "Withdrawals Value - Last 7 Days": "DecimalField",
                  "Withdrawals Count - Last 30 Days": "NumberField",
                  "Withdrawals Value - Last 30 Days": "DecimalField",
                  "Withdrawals Count - Last 90 Days": "NumberField",
                  "Withdrawals Value - Last 90 Days": "DecimalField"
                },
                "Payments Approved Count - Last Day": 'NumberField',
                "Payments Approved Value - Last Day": "CurrencyField",
                "Payments Approved Count - Last 7 Days": 'NumberField',
                "Payments Approved Value - Last 7 Days": "CurrencyField",
                "Payments Approved Count - Last 30 Days": 'NumberField',
                "Payments Approved Value - Last 30 Days": "CurrencyField",
                "Payments Approved Count - Last 90 Days": 'NumberField',
                "Payments Approved Value - Last 90 Days": "CurrencyField"
              }}
            />,
            <MetricComparisonTable
              metricSourceId={METRIC_CREDIT_DAILY_SUMMARY}
              className='slds-m-around_medium'
              renderTemplate={{
                "All": {
                  "Number - Active": "NumberField",
                  "Capital Issued - Active": "CurrencyField",
                  "Outstanding Balance - Active": "CurrencyField",
                  "Loans Issued Count - All Time": "NumberField",
                  "Loans Issued Count - Last Day": "NumberField",
                  "Loans Issued Value - All Time": "CurrencyField",
                  "Loans Issued Value - Last Day": "CurrencyField",
                  "Loans Issued Count - Last 7 Days": "NumberField",
                  "Loans Issued Value - Last 7 Days": "CurrencyField",
                  "Loans Issued Count - Last 30 Days": "NumberField",
                  "Loans Issued Value - Last 30 Days": "CurrencyField",
                  "Loans Issued Count - Last 90 Days": "NumberField",
                  "Loans Issued Value - Last 90 Days": "CurrencyField"
                },
                "Cash": {
                  "Number - Active": "NumberField",
                  "Capital Issued - Active": "CurrencyField",
                  "Outstanding Balance - Active": "CurrencyField",
                  "Loans Issued Count - All Time": "NumberField",
                  "Loans Issued Count - Last Day": "NumberField",
                  "Loans Issued Value - All Time": "CurrencyField",
                  "Loans Issued Value - Last Day": "CurrencyField",
                  "Loans Issued Count - Last 7 Days": "NumberField",
                  "Loans Issued Value - Last 7 Days": "CurrencyField",
                  "Loans Issued Count - Last 30 Days": "NumberField",
                  "Loans Issued Value - Last 30 Days": "CurrencyField",
                  "Loans Issued Count - Last 90 Days": "NumberField",
                  "Loans Issued Value - Last 90 Days": "CurrencyField"
                },
                "Retail": {
                  "Number - Active": "NumberField",
                  "Capital Issued - Active": "CurrencyField",
                  "Outstanding Balance - Active": "CurrencyField",
                  "Loans Issued Count - All Time": "NumberField",
                  "Loans Issued Count - Last Day": "NumberField",
                  "Loans Issued Value - All Time": "CurrencyField",
                  "Loans Issued Value - Last Day": "CurrencyField",
                  "Loans Issued Count - Last 7 Days": "NumberField",
                  "Loans Issued Value - Last 7 Days": "CurrencyField",
                  "Loans Issued Count - Last 30 Days": "NumberField",
                  "Loans Issued Value - Last 30 Days": "CurrencyField",
                  "Loans Issued Count - Last 90 Days": "NumberField",
                  "Loans Issued Value - Last 90 Days": "CurrencyField"
                },
                "Emergency": {
                  "Number - Active": "NumberField",
                  "Capital Issued - Active": "CurrencyField",
                  "Outstanding Balance - Active": "CurrencyField",
                  "Loans Issued Count - All Time": "NumberField",
                  "Loans Issued Count - Last Day": "NumberField",
                  "Loans Issued Value - All Time": "CurrencyField",
                  "Loans Issued Value - Last Day": "CurrencyField",
                  "Loans Issued Count - Last 7 Days": "NumberField",
                  "Loans Issued Value - Last 7 Days": "CurrencyField",
                  "Loans Issued Count - Last 30 Days": "NumberField",
                  "Loans Issued Value - Last 30 Days": "CurrencyField",
                  "Loans Issued Count - Last 90 Days": "NumberField",
                  "Loans Issued Value - Last 90 Days": "CurrencyField"
                },
                "Consolidation": {
                  "Number - Active": "NumberField",
                  "Capital Issued - Active": "CurrencyField",
                  "Outstanding Balance - Active": "CurrencyField",
                  "Loans Issued Count - All Time": "NumberField",
                  "Loans Issued Count - Last Day": "NumberField",
                  "Loans Issued Value - All Time": "CurrencyField",
                  "Loans Issued Value - Last Day": "CurrencyField",
                  "Loans Issued Count - Last 7 Days": "NumberField",
                  "Loans Issued Value - Last 7 Days": "CurrencyField",
                  "Loans Issued Count - Last 30 Days": "NumberField",
                  "Loans Issued Value - Last 30 Days": "CurrencyField",
                  "Loans Issued Count - Last 90 Days": "NumberField",
                  "Loans Issued Value - Last 90 Days": "CurrencyField"
                },
                "Payments Approved Count - Last Day": "NumberField",
                "Payments Approved Value - Last Day": "CurrencyField",
                "Payments Approved Count - Last 7 Days": "NumberField",
                "Payments Approved Value - Last 7 Days": "CurrencyField",
                "Payments Approved Count - Last 30 Days": "NumberField",
                "Payments Approved Value - Last 30 Days": "CurrencyField",
                "Payments Approved Count - Last 90 Days": "NumberField",
                "Payments Approved Value - Last 90 Days": "CurrencyField"
              }}
            />,
            <EmployerKPIMetrics />
          ]}
          selectedTab={this.state.activeTabIndex}
          onClick={this.changeActiveTab}
          tabsClassName='slds-align_absolute-center u-bg-color-white'
          loadContentOnTabClick={true}
        />
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/containers/Metrics.js